/**
 * 🪄 **「누르면 일어나는 마법」의 판정 — 여기 한 곳** (docs/build/35, FR-AR-09).
 *
 * ## 무엇인가
 * AR 장면 하나 = **층(스티커 여러 장) + 누르는 자리 + 손잡이(두 층 섞는 바)**.
 *  · 누르는 자리는 **어느 층에 붙어 있는지**를 안다 — 그 층이 안 보이면 눌리지 않는다.
 *    천마총의 「빛나는 곳 → 전시관 → 유물 → 설명」이 이 하나로 된다(자리가 두 겹).
 *  · 하는 일은 둘뿐: **보여주기**(층 켜고/끄기) · **말하기**(아이용·보호자용 한 줄).
 *
 * ## 🔴 왜 shared 한 곳인가
 * 카메라 AR(아이) · 책 뷰어 평면 모드(아이) · 관리자 미리보기 **셋이 같은 함수**를 쓴다.
 * 화면마다 다시 짜면 「관리자에선 되는데 아이 화면은 안 바뀐다」가 되고, 그건 이 저장소가
 * 반나절을 버린 실제 사고 모양이다(`previewParity.test.ts`).
 *
 * ## 🔴 외부 입력이다 (불변식 7)
 * 이 정의는 D1·정적 매니페스트에서 온다. 모르는 값·범위 밖·없는 층 참조는 **여기서** 걸러 낸다.
 * 화면은 걸러진 것만 받는다.
 *
 * ## 시각 안전
 * 층이 켜지고 꺼질 때는 **`AR_FADE_MS` 이상 서서히**만. 번쩍임 없음(3Hz 규칙은 재생부가 지킨다).
 */

/** 층 전환 최소 시간(ms). 이보다 짧으면 번쩍임으로 보인다 — 화면이 이 값 이하로 내리면 안 된다. */
export const AR_FADE_MS = 300;

export const AR_INTERACT_LIMITS = {
  layers: 12, // 옛 폰 메모리 — 그림 한 장이 판 하나다
  hotspots: 16,
  label: 24,
  /** 출처 표기 한 줄(새 화면 아래). 길면 화면을 가린다. */
  credit: 80,
  say: 160, // 말풍선 한 줄
  /** 누르는 자리 최소 크기(타깃 폭 기준). 0~7세 손가락 + 카메라 손 떨림. */
  minHit: 0.08,
  /** 자리 밖이어도 이만큼 안이면 **가장 가까운 것**이 눌린다(손 떨림 여유). */
  hitSlack: 0.06,
} as const;

/**
 * 🔊 **마법 소리** — 누르는 자리가 낼 수 있는 소리, 이름표는 여기 한 곳.
 *
 * ## 왜 이름표인가(파일 주소가 아니라)
 * 정의는 D1·정적 매니페스트에서 온다 = **외부 입력**(불변식 7). 주소를 그대로 받으면 아무 데나
 * 가리킬 수 있다. 이름만 받고, 그 이름으로 **우리가 아는 소리**를 찾는다.
 *
 * ## 🔴 소리 파일이 없어도 난다
 * 아이 화면은 매니페스트에 `sound.magic.<이름>` 이 있으면 그 파일을, 없으면 **그 자리에서 만든
 * 짧은 소리**를 낸다. 「더미로 먼저 만들고 에셋은 교체만」(CLAUDE.md) 이라 소리가 게시될 때까지
 * 기능이 잠들지 않는다 — 이 저장소가 이미 겪은 「만들어 놓고 닿을 길이 없다」를 피한다.
 */
export const MAGIC_SFX = [
  { id: "tap", label_ko: "톡 (가볍게 누름)" },
  { id: "bite", label_ko: "사각 (베어 물기)" },
  { id: "sparkle", label_ko: "반짝 (별·보물)" },
  { id: "slide", label_ko: "스르륵 (열림·전환)" },
  { id: "water", label_ko: "찰랑 (물)" },
  { id: "light", label_ko: "환하게 (불 켜기)" },
  { id: "leaf", label_ko: "사르락 (잎·풀)" },
] as const;
export type MagicSfx = (typeof MAGIC_SFX)[number]["id"];
const SFX_IDS = new Set<string>(MAGIC_SFX.map((s) => s.id));
export function isMagicSfx(v: unknown): v is MagicSfx {
  return typeof v === "string" && SFX_IDS.has(v);
}

export interface ArRect {
  x: number; // 왼쪽(0~1, 타깃 폭 기준)
  y: number; // 위(0~1, 타깃 높이 기준)
  w: number;
  h: number;
}

/**
 * 🍃 **화면이 만드는 움직임** — 그림 한 장으로 되는 것들. 이름표는 여기 한 곳(소리와 같은 규칙).
 * 모양이 바뀌는 애니는 여기 못 넣는다(띠로 만든다).
 */
export const MAGIC_MOTION = [
  { id: "sway", label_ko: "🍃 살랑살랑 (바람에 흔들림)" },
  { id: "float", label_ko: "🐋 두둥실 (떠서 오르내림)" },
  /** 같은 그림을 두 겹으로 두고 앞뒤를 **엇박**으로 흔들면 바람이 지나가는 깊이가 생긴다(보리밭). */
  { id: "sway2", label_ko: "🍃 살랑살랑 엇박 (뒤 겹용)" },
  /** 넓은 빛띠 한 장을 천천히 옆으로 흘린다(햇살). 8초에 한 번 왕복 — 느려서 움직임보다 「살아 있음」에 가깝다. */
  { id: "drift", label_ko: "🌤 스르르 (옆으로 천천히)" },
  /**
   * 🌬 **바람결** — 한쪽으로 **눕다가** 천천히 돌아온다(좌우로 까딱이는 살랑살랑과 다르다). 밭 여러 줄에
   * 엇박(`motionPhase`)을 달리 주면 바람이 밭을 **건너온다**(2026-09-11 · 22쪽 보리밭).
   */
  { id: "gust", label_ko: "🌬 바람결 (한쪽으로 눕다 돌아옴)" },
] as const;
export type MagicMotion = (typeof MAGIC_MOTION)[number]["id"];

/**
 * 🌾 **엇박** — 움직임을 몇 ms **앞서** 둘까(0~`MAGIC_MOTION_PHASE_MAX`).
 *
 * ## 왜 (2026-09-11 · 22쪽 보리밭 「멋진 배경에 보리밭이 바람에 날리는」)
 * 밭을 **앞뒤 여러 줄**로 나눠 줄마다 조금씩 엇박으로 흔들면 바람이 **밭을 건너오는** 물결이 된다.
 * 「엇박」 이름표(sway2)는 반 박자 하나뿐이라 두 줄까지만 됐다.
 * 🔴 기다리지 않는다 — 음수 지연이라 켜지자마자 **이미 그만큼 앞선 자세로** 흔들리는 중이다.
 *    (양수로 늦추면 그동안 가만히 서 있다가 뒤늦게 움직인다 — 「고장」으로 보인다)
 */
export const MAGIC_MOTION_PHASE_MAX = 2400;
/** 세 화면(책·카메라 AR·관리자 미리보기)이 층에 붙이는 CSS 변수 — CSS 는 `var(--motion-phase, 0s)` 로 받는다. */
export function layerMotionVars(l: Pick<ArLayerDef, "motion" | "motionPhase">): Record<string, string> {
  if (!l.motion || !l.motionPhase) return {};
  return { "--motion-phase": `-${l.motionPhase}ms` };
}
const MOTION_IDS = new Set<string>(MAGIC_MOTION.map((m) => m.id));
export function isMagicMotion(v: unknown): v is MagicMotion {
  return typeof v === "string" && MOTION_IDS.has(v);
}

/**
 * ✨ **등장 연출** — 층이 **켜지는 순간** 한 번만 움직인다(움직임 `motion` 은 켜져 있는 내내다).
 *
 * ## 왜 (2026-09-11 · 《폴짝》 7장면을 다시 보고)
 * 전환이 전부 「300ms 투명도 페이드」 하나였다. 전시관이 열리든 얼굴이 물에서 나오든 같은
 * 방식이라 「짠」 하는 맛이 없었다. 그림을 새로 안 굽고 **화면이** 넣는다 — 크레딧 0.
 *
 * `enterDelay` 로 **차례로**(유물 넷이 0·150·300·450ms) 놓을 수 있다.
 * 🔴 켜지는 순간의 연출이라 **처음부터 보이는 층에는 안 붙는다**(`layerEnter` 가 거른다) —
 *    마법을 열자마자 배경이 튀어 오르면 놀이가 아니라 소음이다.
 */
export const MAGIC_ENTER = [
  { id: "grow", label_ko: "🔍 커지며 (작게서 제 크기로)" },
  { id: "rise", label_ko: "⬆️ 떠오르며 (아래에서 위로)" },
] as const;
export type MagicEnter = (typeof MAGIC_ENTER)[number]["id"];
const ENTER_IDS = new Set<string>(MAGIC_ENTER.map((m) => m.id));
export function isMagicEnter(v: unknown): v is MagicEnter {
  return typeof v === "string" && ENTER_IDS.has(v);
}
/** 등장을 늦출 수 있는 최대(ms). 이보다 길면 아이가 「안 되나?」 하고 또 누른다. */
export const MAGIC_ENTER_DELAY_MAX = 1500;

/**
 * 이 층이 켜질 때 **무슨 연출을 몇 ms 뒤에** 할까 — 세 화면(책·카메라 AR·관리자 미리보기)이 이걸 쓴다.
 * 안 켜져 있거나(`shown` 0) 처음부터 보이는 층이면 `null`.
 */
/**
 * 누른 뒤 **화면이 다 바뀌기까지** 몇 ms 인가 — 전환(`AR_FADE_MS`) + 가장 늦게 등장하는 층의 늦춤.
 * 걸어 보는 검사(`magic:walk`)가 이만큼 기다린 뒤 읽는다 — 늦춤을 모르고 읽으면 아직 반투명한 층을
 * 「안 보인다」고 잘못 잡는다(2026-09-11 전시관 날개 장식이 그랬다).
 */
export function enterSettleMs(def: Pick<ArInteract, "layers">): number {
  return AR_FADE_MS + Math.max(0, ...def.layers.map((l) => l.enterDelay ?? 0));
}

export function layerEnter(l: Pick<ArLayerDef, "startVisible" | "enter" | "enterDelay">, shown: number): { enter: MagicEnter; delayMs: number } | null {
  if (!(shown > 0) || l.startVisible || !l.enter) return null;
  return { enter: l.enter, delayMs: l.enterDelay ?? 0 };
}

export interface ArLayerDef {
  id: string; // `[a-z0-9_]{1,24}` — 그림 자리 이름에 들어간다(arKeys.arLayerImgKey)
  label_ko: string;
  /** 처음부터 보일까. 눌러야 보이는 층은 false. */
  startVisible: boolean;
  /**
   * 🔴 **그림을 쪽의 어디에 놓나**(0~1). 없으면 쪽 전체.
   *
   * 이게 없으면 운영자가 **쪽 크기에 미리 맞춰 놓은 큰 판**만 올릴 수 있다 — 금관 한 장을
   * 그대로 올리면 쪽 한가운데에 거대하게 뜬다. 그러면 자리를 옮기려고 **그림을 다시 만들어야**
   * 하고, 그건 이 저장소가 「다시 올리라면 안 한다」로 이미 배운 실패다.
   * 자리는 **정의에 있어야** 관리자에서 끌어 옮길 수 있다.
   */
  rect?: ArRect;
  /**
   * 🖼️ **새 화면으로 열기** (2026-09-09). 쪽 위에 작게 겹치는 게 아니라 **쪽을 덮고 크게** 보여 준다 —
   * 기획안의 「보리밭 애니메이션으로 전환」「천마총 전시관」이 이것이다(7쪽 창문의 「확대되어」도 같은 뜻).
   * 이 층이 보이는 동안은 새 화면 상태다: 배경이 깔리고, `full` 층들만 그려지고, 그 층들에 붙은 자리만 눌린다.
   * 🔴 `full` 층의 자리(rect)와 거기 붙은 누르는 자리는 **쪽이 아니라 새 화면 기준** 0~1 이다.
   * 「← 쪽으로」는 처음 상태로 되돌린다(`goBack`) — 아이가 막다른 데 남지 않게.
   */
  full?: boolean;
  /**
   * 🍃 **한 장으로 움직이기** — 그림은 한 장인데 화면이 흔든다.
   *
   * ## 🔴 왜 띠 대신인가 (2026-09-10)
   * 보리밭 흔들림은 **같은 그림을 8번 휘어** 이어 붙인 띠였다. 974KB — 교실 회선에서 **8.6초**다.
   * 그런데 그 8장의 차이는 「위로 갈수록 옆으로 조금」뿐이다. 화면이 할 수 있는 일을 **파일로
   * 나른 것**이라, 한 장(158KB)만 보내고 흔드는 일은 화면에 맡기면 **6분의 1**이 되고
   * 게다가 **더 부드럽다**(8칸으로 끊기지 않는다).
   *
   * 모양이 바뀌는 것(꼬투리가 열림·빵을 베어 뭄)은 여전히 띠여야 한다 — 그건 화면이 못 만든다.
   * 🔴 뿌리는 그대로 두고 **위만** 흔든다(아래를 축으로). 통째로 기울면 다발이 미끄러져 보인다.
   */
  motion?: MagicMotion;
  /** 🌾 엇박(ms) — 움직임을 이만큼 앞선 자세로. 움직임이 있을 때만 뜻이 있다(`layerMotionVars`). */
  motionPhase?: number;
  /**
   * 🔗 **다른 층을 따라 보인다** — 그 층이 보이는 만큼만 이 층도 보인다(손잡이로 섞이는 층도).
   *
   * ## 왜 (2026-09-11 · 7쪽·30쪽 별 반짝임)
   * 「밤이 되면 별이 반짝」은 별 층이 **밤 층을 따라야** 한다. 밤은 손잡이로 0~1 로 섞이는데,
   * 층 켜고 끄기는 0/1 뿐이라 별이 낮에도 깜빡이거나 손잡이 중간에서 툭 켜졌다.
   * 따라가는 층은 제 스위치(visible)도 그대로 갖는다 — 자리로 끌 수 있다.
   * 🔴 한 단계만 — 따라가는 층을 또 따라가지 못한다(고리가 생긴다). `normalizeArInteract` 가 거른다.
   */
  follow?: string;
  /**
   * 🔗 따라갈 때 **곡선** — `early` 는 앞 절반에서 다 켜지고(창문 불이 먼저), `late` 는 뒤 절반에서
   * 켜진다(탑 전체는 나중에). 없으면 그대로(1:1). 30쪽 첨성대 「창문 먼저, 탑은 나중에」가 이것이다.
   */
  followEase?: "early" | "late";
  /**
   * 🔄 **끌면 돌아간다** (2026-09-12 · 27쪽 얼굴무늬 수막새 360°).
   * 띠의 장들이 **시간이 아니라 각도**다 — 3D 모델을 10~15도씩 돌려 찍은 24~36장. 화면은 띠를 자동으로
   * 넘기지 않고, 아이가 손가락으로 끄는 만큼 장을 고른다(`spinFrame`). 3D 엔진 없이 세 화면이 같은 띠로 돈다.
   * 🔴 새 화면(`full`)에서만 뜻이 있다 — 쪽 위에서는 끌기가 책 넘김과 부딪힌다. `arInteractIssues` 가 말해 준다.
   */
  spin?: boolean;
  /**
   * 🔄 **저절로 돌다가, 끌면 손을 따른다** (2026-09-12 · 운영자 「끌어서 돌리기와 자동 회전을 고르게」).
   * 켜면 새 화면이 열릴 때 천천히 돈다(`SPIN_AUTO_FPS`, 24장이면 한 바퀴 6초). 아이가 한 번 끌면 그 뒤로는
   * 끈 만큼만 돈다 — 손을 뗀 자리에서 멈춘다(다시 저절로 돌지 않는다: 아이가 「멈췄다」고 느낀 것을 뒤집지 않는다).
   * `spin` 이 없으면 뜻이 없다. 「움직임 줄이기」에서는 CSS 가 첫 장에 멈춘다(끌기는 그대로 된다).
   */
  spinAuto?: boolean;
  /**
   * 🏷 **출처 표기** — 이 층이 새 화면에 보이는 동안 화면 아래에 작게 나온다. 남의 자료(CC BY 등)를 쓸 때 의무다.
   * 예: 「국가유산청(KHS) 3D 스캔 · CC BY 4.0」. 사실 한 줄(지름 11.5cm)도 같이 적을 수 있다.
   */
  credit_ko?: string;
  /** ✨ 켜지는 순간의 연출(한 번). 이름표는 `MAGIC_ENTER` 한 곳. */
  enter?: MagicEnter;
  /** ✨ 등장을 몇 ms 늦출까(0~`MAGIC_ENTER_DELAY_MAX`) — 차례로 놓을 때. */
  enterDelay?: number;
}

/**
 * 🔴 **소리는 두 동작 모두에 붙는다.** 빵 한 입(보여주기)에도, 유물 설명(말하기)에도 난다.
 * 아이 손가락이 만든 소리라 **자동재생이 아니다**(불변식 6은 자동재생을 막는 규칙이다).
 */
export type ArAction =
  /**
   * `guardian_ko` 는 보여주기에도 붙는다(2026-10-03) — 마법 26장면 중 12장면이 보여주기뿐이라
   * 「보호자 함께 읽기 질문」(조치계획서 ■1 · 10. 31.)을 넣을 칸이 **없었다.** 누르면 아이 말풍선 없이 보호자 줄만 뜬다.
   */
  | { kind: "show"; on: string[]; off: string[]; guardian_ko?: string; sfx?: MagicSfx }
  | { kind: "say"; child_ko: string; guardian_ko?: string; focus?: string; sfx?: MagicSfx };

export interface ArHotspot {
  id: string;
  label_ko: string;
  /** 어느 층에 붙어 있나 — 그 층이 보일 때만 눌린다. */
  layer: string;
  rect: ArRect;
  action: ArAction;
}

export interface ArSlider {
  label_ko: string;
  a: string; // 왼쪽 끝 층
  b: string; // 오른쪽 끝 층
  iconA?: string; // 예: ☀️
  iconB?: string; // 예: 🌙
}

export interface ArInteract {
  layers: ArLayerDef[];
  hotspots: ArHotspot[];
  slider?: ArSlider;
  /** 「돌아가기」를 눌렀을 때 — 없으면 처음 상태로 되돌린다. */
  back?: { kind: "show"; on: string[]; off: string[] };
}

const ID_RE = /^[a-z0-9_]{1,24}$/;
const num = (v: unknown, def: number, lo: number, hi: number): number =>
  typeof v === "number" && Number.isFinite(v) ? Math.max(lo, Math.min(hi, v)) : def;
const str = (v: unknown, max: number): string =>
  typeof v === "string" ? v.replace(/\s+/g, " ").trim().slice(0, max) : "";
const ids = (v: unknown, known: Set<string>): string[] =>
  Array.isArray(v) ? [...new Set(v.filter((x): x is string => ID_RE.test(String(x)) && known.has(x)))] : [];

/** 층 그림이 이보다 작으면 아이 눈에 티끌이다. */
export const AR_LAYER_MIN = 0.03;

/**
 * 🔴 **새 화면에서 실제로 다 보이는 띠** (2026-09-09, 운영 화면을 재서 얻었다).
 *
 * 새 화면(`full`)에도 위에는 **「← 쪽으로」**(아래끝 0.136), 아래에는 **말풍선·손잡이 바**
 * (`magic__foot`)가 겹쳐 있다. 무대는 늘 쪽 전체여야 해서(자리 좌표가 밀리지 않게)
 * 이것들을 **겹쳐** 놓기 때문이다 — 그래서 y 0.9 에 놓은 유물은 아래가 잘려 보였다.
 *
 * 🔴 **아래 끝이 둘인 이유** (2026-09-09 두 번째 수정). 처음엔 말풍선이 **없는** 상태에서 재서
 * 0.83 하나였다. 그런데 그 화면에서 아이가 하는 일이 바로 **유물을 눌러 설명을 보는 것**이다 —
 * 누르는 순간 말풍선이 두 줄로 열리며 바 위끝이 **0.688** 로 올라온다(Pixel 7, 396×396 무대
 * 실측). 그러면 0.83 에 놓은 유물이 **제 설명을 가린다**(운영자 신고: 「유물들이 글자를 가려요」).
 *
 * 그렇다고 0.68 하나로 낮추면 **말풍선이 없는 화면까지** 공연히 좁아진다(보리밭·팥 꼬투리는
 * 누르면 그림만 바뀌지 말은 안 한다). 그래서 **그 새 화면에서 말이 나올 수 있는지**를 보고 고른다.
 */
export const MAGIC_FULL_SAFE_TOP = 0.15;
/** 아래에 **바만** 있을 때(「← 처음으로」·「✕ 닫기」). */
export const MAGIC_FULL_SAFE_BOTTOM = 0.83;
/** 그 화면에서 **말풍선이 열릴 수 있을 때** — 두 줄 + 「보호자용」까지 편 높이로 잰 값. */
export const MAGIC_FULL_SAFE_BOTTOM_SAY = 0.68;

/**
 * 층의 놓을 자리 — **넷이 다 있을 때만** 쓴다. 하나라도 없으면 `null`(= 쪽 전체).
 * 반쯤 적힌 자리를 0 으로 메우면 그림이 왼쪽 위 구석에 찌그러져 붙는다.
 */
const rect = (v: unknown): ArRect | null => {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const has = (k: string) => typeof o[k] === "number" && Number.isFinite(o[k] as number);
  if (!has("x") || !has("y") || !has("w") || !has("h")) return null;
  const w = num(o.w, AR_LAYER_MIN, AR_LAYER_MIN, 1);
  const h = num(o.h, AR_LAYER_MIN, AR_LAYER_MIN, 1);
  return { x: num(o.x, 0, 0, 1 - w), y: num(o.y, 0, 0, 1 - h), w, h };
};

/**
 * 외부 입력 → 믿을 수 있는 정의. 못 쓰는 조각은 **버린다**(고치지 않는다).
 * 층이 하나도 없으면 `null` — 「마법이 없는 장면」이다(기존 그림 한 장짜리와 같다).
 *
 * 🔴 여기서 버려진 것은 **조용히** 사라진다. 그래서 관리자는 저장 전에 `arInteractIssues` 로
 * 「무엇이 왜 안 되는지」를 보여 준다 — 이 함수는 마지막 안전망이지 안내판이 아니다.
 */
export function normalizeArInteract(raw: unknown): ArInteract | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;

  const layers: ArLayerDef[] = [];
  const seen = new Set<string>();
  for (const l of Array.isArray(r.layers) ? r.layers : []) {
    if (!l || typeof l !== "object") continue;
    const o = l as Record<string, unknown>;
    if (!ID_RE.test(String(o.id)) || seen.has(String(o.id))) continue;
    if (layers.length >= AR_INTERACT_LIMITS.layers) break;
    seen.add(String(o.id));
    const box = rect(o.rect);
    layers.push({
      id: String(o.id),
      label_ko: str(o.label_ko, AR_INTERACT_LIMITS.label) || String(o.id),
      startVisible: o.startVisible !== false,
      ...(box ? { rect: box } : {}),
      ...(o.full === true ? { full: true } : {}),
      ...(isMagicMotion(o.motion) ? { motion: o.motion } : {}), // 모르는 이름은 버린다(안 움직일 뿐)
      // 엇박은 0~최대 사이의 정수만. 움직임 없이 엇박만 있으면 뜻이 없어 버린다.
      ...(isMagicMotion(o.motion) && typeof o.motionPhase === "number" && Number.isFinite(o.motionPhase) && o.motionPhase > 0
        ? { motionPhase: Math.min(MAGIC_MOTION_PHASE_MAX, Math.round(o.motionPhase)) }
        : {}),
      ...(o.spin === true ? { spin: true } : {}),
      ...(o.spin === true && o.spinAuto === true ? { spinAuto: true } : {}),
      ...(str(o.credit_ko, AR_INTERACT_LIMITS.credit) ? { credit_ko: str(o.credit_ko, AR_INTERACT_LIMITS.credit) } : {}),
      ...(isMagicEnter(o.enter) ? { enter: o.enter } : {}),
      // 늦춤은 0~최대 사이의 정수만. 이름 없이 늦춤만 있으면 뜻이 없어 버린다.
      ...(isMagicEnter(o.enter) && typeof o.enterDelay === "number" && Number.isFinite(o.enterDelay) && o.enterDelay > 0
        ? { enterDelay: Math.min(MAGIC_ENTER_DELAY_MAX, Math.round(o.enterDelay)) }
        : {}),
    });
  }
  if (layers.length === 0) return null;
  // 🔗 따라갈 층: 있는 층이고, 자기가 아니고, **그 층은 아무도 안 따라가는** 층이어야 한다(한 단계)
  const rawFollow = new Map<string, string>();
  for (const l of Array.isArray(r.layers) ? r.layers : []) {
    const o = (l ?? {}) as Record<string, unknown>;
    if (typeof o.follow === "string" && seen.has(String(o.id))) rawFollow.set(String(o.id), o.follow);
  }
  const rawEase = new Map<string, unknown>();
  for (const l of Array.isArray(r.layers) ? r.layers : []) {
    const o = (l ?? {}) as Record<string, unknown>;
    rawEase.set(String(o.id), o.followEase);
  }
  for (const l of layers) {
    const f = rawFollow.get(l.id);
    if (f && f !== l.id && seen.has(f) && !rawFollow.has(f)) {
      l.follow = f;
      const e = rawEase.get(l.id);
      if (e === "early" || e === "late") l.followEase = e; // 모르는 값은 그대로(1:1)
    }
  }

  const hotspots: ArHotspot[] = [];
  const hseen = new Set<string>();
  for (const h of Array.isArray(r.hotspots) ? r.hotspots : []) {
    if (!h || typeof h !== "object") continue;
    const o = h as Record<string, unknown>;
    if (!ID_RE.test(String(o.id)) || hseen.has(String(o.id))) continue;
    if (!seen.has(String(o.layer))) continue; // 붙을 층이 없다
    if (hotspots.length >= AR_INTERACT_LIMITS.hotspots) break;
    const action = normalizeAction(o.action, seen);
    if (!action) continue;
    const rr = (o.rect ?? {}) as Record<string, unknown>;
    const w = num(rr.w, 0, AR_INTERACT_LIMITS.minHit, 1);
    const hgt = num(rr.h, 0, AR_INTERACT_LIMITS.minHit, 1);
    hseen.add(String(o.id));
    hotspots.push({
      id: String(o.id),
      label_ko: str(o.label_ko, AR_INTERACT_LIMITS.label) || String(o.id),
      layer: String(o.layer),
      rect: { x: num(rr.x, 0, 0, 1 - w), y: num(rr.y, 0, 0, 1 - hgt), w, h: hgt },
      action,
    });
  }

  let slider: ArSlider | undefined;
  if (r.slider && typeof r.slider === "object") {
    const s = r.slider as Record<string, unknown>;
    if (seen.has(String(s.a)) && seen.has(String(s.b)) && s.a !== s.b) {
      slider = {
        label_ko: str(s.label_ko, AR_INTERACT_LIMITS.label) || "바꾸기",
        a: String(s.a),
        b: String(s.b),
        iconA: str(s.iconA, 4) || undefined,
        iconB: str(s.iconB, 4) || undefined,
      };
    }
  }

  const back = normalizeAction(r.back, seen);
  return {
    layers,
    hotspots,
    ...(slider ? { slider } : {}),
    ...(back && back.kind === "show" ? { back } : {}),
  };
}

function normalizeAction(raw: unknown, known: Set<string>): ArAction | null {
  if (!raw || typeof raw !== "object") return null;
  const a = raw as Record<string, unknown>;
  const sfx = isMagicSfx(a.sfx) ? { sfx: a.sfx } : {}; // 모르는 소리 이름은 버린다(조용해질 뿐)
  if (a.kind === "show") {
    const on = ids(a.on, known);
    const off = ids(a.off, known);
    const guardian = str(a.guardian_ko, AR_INTERACT_LIMITS.say);
    return on.length || off.length ? { kind: "show", on, off, ...(guardian ? { guardian_ko: guardian } : {}), ...sfx } : null;
  }
  if (a.kind === "say") {
    const child = str(a.child_ko, AR_INTERACT_LIMITS.say);
    if (!child) return null;
    const guardian = str(a.guardian_ko, AR_INTERACT_LIMITS.say);
    const focus = known.has(String(a.focus)) ? String(a.focus) : undefined;
    return { kind: "say", child_ko: child, ...(guardian ? { guardian_ko: guardian } : {}), ...(focus ? { focus } : {}), ...sfx };
  }
  return null;
}

/**
 * 🩺 **무엇이 반만 만들어졌나** — 관리자 저장 전 안내 + [✅ 출시 준비].
 *
 * `normalizeArInteract` 가 조용히 버리는 것들을 **말로** 돌려준다. 정규화 **전**의 원본을 넣는다.
 * 빈 배열이면 문제가 없다.
 */
export function arInteractIssues(raw: unknown): string[] {
  const out: string[] = [];
  if (!raw || typeof raw !== "object") return out;
  const r = raw as Record<string, unknown>;
  const layers = Array.isArray(r.layers) ? r.layers : [];
  const known = new Set<string>();
  for (const l of layers) {
    const id = String((l as Record<string, unknown>)?.id ?? "");
    if (!ID_RE.test(id)) out.push(`층 이름「${id || "(빈 이름)"}」— 영문 소문자·숫자·_ 만, 24자까지`);
    else if (known.has(id)) out.push(`층「${id}」이 두 번 있어요`);
    else known.add(id);
  }
  if (known.size === 0) {
    out.push("층이 하나도 없어요 — 스티커를 한 장이라도 올려야 마법이 돼요");
    return out;
  }
  /**
   * 🔴 새 화면(`full`)의 자리가 **가려지는 띠**에 걸쳐 있으면 알려 준다.
   * 위 「← 쪽으로」·아래 말풍선 바가 겹쳐 있어 거기 놓은 그림은 잘려 보인다(MAGIC_FULL_SAFE_*).
   */
  const fullIds = new Set<string>();
  for (const l of layers) {
    const o = (l ?? {}) as Record<string, unknown>;
    if (o.full === true) fullIds.add(String(o.id ?? ""));
  }
  const hotspots = Array.isArray(r.hotspots) ? r.hotspots : [];
  /**
   * 그 새 화면에서 **말풍선이 열릴 수 있나** — 새 화면 층에 붙은 자리 중 「말하기」가 있는가.
   * 있으면 아래 띠가 그만큼 두꺼워지므로 자리를 더 위로 올려야 한다.
   */
  const canSay = hotspots.some((h) => {
    const o = (h ?? {}) as Record<string, unknown>;
    return fullIds.has(String(o.layer)) && ((o.action ?? {}) as Record<string, unknown>).kind === "say";
  });
  const bottom = canSay ? MAGIC_FULL_SAFE_BOTTOM_SAY : MAGIC_FULL_SAFE_BOTTOM;
  for (const l of layers) {
    const o = (l ?? {}) as Record<string, unknown>;
    // 🔄 끌어 돌리는 층은 새 화면에서만 — 쪽 위에서는 끌기가 책 넘김과 부딪힌다
    if (o.spin === true && o.full !== true) {
      out.push(`층「${str(o.label_ko, AR_INTERACT_LIMITS.label) || String(o.id ?? "")}」— 끌어 돌리기는 「새 화면으로 열기」를 켜야 돼요. 쪽 위에서는 끌기가 책 넘김과 부딪혀요`);
    }
    if (o.full !== true) continue;
    const box = rect(o.rect);
    if (!box) continue; // 자리 없음 = 새 화면 전체(배경) — 가려도 되는 것이다
    const name = str(o.label_ko, AR_INTERACT_LIMITS.label) || String(o.id ?? "");
    // 🔴 딱 맞게 놓은 자리를 경계 오차로 잡지 않는다 — `0.52 + 0.31` 은 0.8300000000000001 이다
    const EPS = 0.002;
    /*
     * 🌾 **화면 끝에 닿은 층은 화면 밖으로 이어지는 풍경**이다 — 자리 없는 배경처럼 가려도 된다
     * (2026-09-11 · 22쪽 보리 앞줄은 땅에서 솟아 아래 끝까지 간다). 이걸 잡으면 관리자가 운영자에게
     * 「세로를 옮겨 주세요」라고 **틀린 일을 시킨다**(옮기면 보리가 공중에 뜬다).
     * 🔴 단 **누르는 자리가 붙은 층**은 아니다 — 가리면 못 누른다. 그런 층은 끝에 닿아도 잡는다.
     */
    const touchesEdge = box.y <= EPS || box.y + box.h >= 1 - EPS;
    const pressed = hotspots.some((h) => String(((h ?? {}) as Record<string, unknown>).layer) === String(o.id));
    if (touchesEdge && !pressed) continue;
    if (box.y < MAGIC_FULL_SAFE_TOP - EPS || box.y + box.h > bottom + EPS) {
      out.push(
        `새 화면 층「${name}」— ${canSay ? "말풍선·" : ""}위아래 단추에 가려요. ` +
          `세로를 ${MAGIC_FULL_SAFE_TOP}~${bottom} 안으로 옮겨 주세요`,
      );
    }
  }

  for (const l of layers) {
    const o = (l ?? {}) as Record<string, unknown>;
    if (typeof o.follow !== "string") continue;
    const name = str(o.label_ko, AR_INTERACT_LIMITS.label) || String(o.id ?? "");
    if (!known.has(o.follow)) out.push(`층「${name}」— 따라갈 층「${o.follow}」이 없어요`);
    else if (o.follow === String(o.id)) out.push(`층「${name}」— 자기 자신을 따라갈 수 없어요`);
  }

  const reachable = new Set<string>();
  for (const h of hotspots) {
    const o = (h ?? {}) as Record<string, unknown>;
    const id = String(o.id ?? "");
    const name = str(o.label_ko, AR_INTERACT_LIMITS.label) || id || "(이름 없음)";
    if (!known.has(String(o.layer))) out.push(`누르는 자리「${name}」— 붙을 층「${String(o.layer ?? "")}」이 없어요`);
    /**
     * 🔴 **너무 아래에 놓으면 보이는데 안 눌린다** (2026-09-09 · 2026-09-10, 두 번 겪었다).
     *
     * 무대 아래에는 말풍선·손잡이 바(`.magic__foot`)가 **겹쳐** 있다. 그 바는 **픽셀 키가 고정**이라
     * 화면이 작을수록 무대에서 차지하는 **비율이 커진다** — 360×740 폰에서 손잡이가 있는 장면은
     * 아래 0.69 부터를 덮는다. 실제로 31쪽 첨성대를 아이가 못 눌렀고, 전에는 유물 둘이
     * 제 설명 밑에 깔렸다. 자리를 그리는 사람에게는 **멀쩡히 보인다** — 그래서 여기서 말해 준다.
     *
     * 재는 것은 **가운데**다. 손가락은 가운데에 닿고, 걸어 보는 검사(`magic:walk`)도 가운데를 누른다.
     */
    const hr = rect(o.rect);
    if (hr && hr.y + hr.h / 2 > MAGIC_FULL_SAFE_BOTTOM_SAY + 0.002) {
      out.push(
        `누르는 자리「${name}」— 너무 아래예요(가운데 ${(hr.y + hr.h / 2).toFixed(2)}). ` +
          `작은 폰에서 아래 바에 가려 **안 눌립니다** — 가운데를 ${MAGIC_FULL_SAFE_BOTTOM_SAY} 위로 올려 주세요`,
      );
    }
    const a = (o.action ?? {}) as Record<string, unknown>;
    if (a.kind === "show") {
      for (const k of ["on", "off"] as const) {
        for (const l of Array.isArray(a[k]) ? (a[k] as unknown[]) : []) {
          if (!known.has(String(l))) out.push(`누르는 자리「${name}」— ${k === "on" ? "켤" : "끌"} 층「${String(l)}」이 없어요`);
          else if (k === "on") reachable.add(String(l));
        }
      }
      if (!(a.on as unknown[])?.length && !(a.off as unknown[])?.length)
        out.push(`누르는 자리「${name}」— 켜거나 끌 층을 하나도 안 골랐어요`);
    } else if (a.kind === "say") {
      if (!str(a.child_ko, AR_INTERACT_LIMITS.say)) out.push(`누르는 자리「${name}」— 아이에게 할 말이 비었어요`);
    } else {
      out.push(`누르는 자리「${name}」— 무엇을 할지(보여주기/말하기)를 안 골랐어요`);
    }
    // 🔴 소리 이름을 잘못 적으면 **조용히** 사라진다 — 저장 전에 말해 준다
    if (a.sfx !== undefined && !isMagicSfx(a.sfx))
      out.push(`누르는 자리「${name}」— 「${String(a.sfx)}」는 모르는 소리예요(소리 없이 나가요)`);
    const rr = (o.rect ?? {}) as Record<string, unknown>;
    if (num(rr.w, 0, 0, 1) < AR_INTERACT_LIMITS.minHit || num(rr.h, 0, 0, 1) < AR_INTERACT_LIMITS.minHit)
      out.push(`누르는 자리「${name}」— 너무 작아요(아이 손가락이 못 눌러요). 조금 크게 그려 주세요`);
  }
  // 눌러야 보이는 층인데 아무도 안 켜 준다 → 아이가 영영 못 본다
  for (const l of layers) {
    const o = l as Record<string, unknown>;
    const id = String(o.id ?? "");
    if (!known.has(id) || o.startVisible !== false) continue;
    const bySlider = r.slider && typeof r.slider === "object" && [String((r.slider as Record<string, unknown>).a), String((r.slider as Record<string, unknown>).b)].includes(id);
    if (!reachable.has(id) && !bySlider) out.push(`층「${str(o.label_ko, 24) || id}」— 눌러야 보이는데 켜 주는 자리가 없어요(아이가 영영 못 봐요)`);
  }
  if (r.slider && typeof r.slider === "object") {
    const s = r.slider as Record<string, unknown>;
    if (!known.has(String(s.a)) || !known.has(String(s.b))) out.push("손잡이 — 양쪽 끝 층 중 없는 것이 있어요");
    else if (s.a === s.b) out.push("손잡이 — 양쪽 끝이 같은 층이에요(바꿔도 아무것도 안 달라져요)");
  }
  return out;
}

/** 지금 보이는 층들. 손잡이가 있으면 `mix`(0=왼쪽 층, 1=오른쪽 층). */
export interface ArState {
  visible: Set<string>;
  mix: number;
  /** 마지막으로 「말하기」한 자리 — 화면이 말풍선을 띄운다. */
  said?: { hotspot: string; child_ko: string; guardian_ko?: string; focus?: string };
  /**
   * 👨‍👩‍👧 마지막으로 누른 **보여주기** 자리의 보호자 문구 — 아이 말풍선 없이 보호자 줄만 띄운다(2026-10-03).
   * 「말하기」는 위 `said.guardian_ko` 가 맡는다(둘이 같이 뜨지 않는다).
   */
  hint?: { hotspot: string; guardian_ko: string };
  /**
   * 🔁 **이미 보이는 층을 또 켜라고 한 횟수** — 한 번만 도는 띠(별똥별)를 **누를 때마다** 다시 돌린다.
   * 화면은 띠 `<img>` 를 「꺼짐→켜짐」에만 새로 붙였다. 그래서 켜진 채로 또 누르면 **아무 일도 없었다**
   * (2026-09-11 · 25쪽 「별이 반짝여요」). 화면은 이 수를 `layerPlayKey` 로 key 에 넣는다.
   */
  plays?: Record<string, number>;
  /** 🔄 끌어 돌린 각도(한 바퀴 = 1) — 층마다. 없으면 0(첫 장 = 정면). */
  spin?: Record<string, number>;
  /**
   * 🧊 3D 로 볼 때의 시점 — 층마다. `tilt` 는 위아래로 기울인 각도(도, 음수면 위에서 내려다봄),
   * `zoom` 은 두 손가락으로 키운 배율. 없으면 정면·원래 크기. 띠(2D)는 `zoom` 만 쓴다(기울일 면이 없다).
   */
  view?: Record<string, SpinView>;
}

/** 🧊 3D 시점 한 벌. */
export interface SpinView {
  /** 위아래 기울기(도). `SPIN_TILT_MAX` 안 */
  tilt: number;
  /** 배율. `SPIN_ZOOM_MIN`~`SPIN_ZOOM_MAX` 안 */
  zoom: number;
}

/** 무대 너비만큼 끌면 몇 바퀴 도나 — 1 이면 화면 끝에서 끝까지 끌 때 한 바퀴. 0~7세 손 크기에 맞춘 값. */
export const SPIN_TURNS_PER_WIDTH = 1;
/** 이보다 덜 움직였으면 「끌기」가 아니라 「누르기」다(무대 너비 기준 비율). 손 떨림보다 크고 의도한 끌기보다 작게. */
export const SPIN_TAP_SLACK = 0.02;

/**
 * 🔄 **끌면 돌아간다** — 세 화면이 같은 셈으로 장을 고른다.
 * `dx` 는 무대 너비 기준(-1~1). 왼쪽으로 끌면 오른쪽 옆면이 보이게(물건을 손으로 돌리는 방향).
 */
export function spinDrag(state: ArState, layerId: string, dx: number): ArState {
  if (!Number.isFinite(dx) || dx === 0) return state;
  const cur = state.spin?.[layerId] ?? 0;
  let next = (cur - dx * SPIN_TURNS_PER_WIDTH) % 1;
  if (next < 0) next += 1;
  return { ...state, spin: { ...state.spin, [layerId]: next } };
}

/** 저절로 돌 때 초당 장 수 — 24장이면 한 바퀴 6초. 느려야 「전시된 유물」이지 「돌아가는 광고」가 아니다. 3Hz 와 무관(번쩍임 없음). */
export const SPIN_AUTO_FPS = 4;

/**
 * 🔄 이 층이 지금 **어떻게 도나** — `"auto"`(저절로, 아직 안 끌었다) · `"drag"`(끈 만큼) · `null`(도는 층이 아니다).
 * 세 화면이 이 값으로 띠 스타일을 고른다: auto 는 시간으로 넘기는 `stripStyle`, drag 는 `stripFrameStyle`.
 */
export function spinMode(l: Pick<ArLayerDef, "spin" | "spinAuto">, state: Pick<ArState, "spin">, layerId: string): "auto" | "drag" | null {
  if (!l.spin) return null;
  if (l.spinAuto && state.spin?.[layerId] === undefined) return "auto";
  return "drag";
}

/** 🔄 각도를 바로 놓는다(0~1) — 저절로 돌던 것을 손으로 잡는 순간, 그 자리에서 이어받게. */
export function spinSet(state: ArState, layerId: string, turn: number): ArState {
  if (!Number.isFinite(turn)) return state;
  let t = turn % 1;
  if (t < 0) t += 1;
  return { ...state, spin: { ...state.spin, [layerId]: t } };
}

/** 🧊 위아래로 얼마나 기울일 수 있나(도). 60 을 넘기면 뒤집혀 보이고 아이가 원래대로 못 돌아온다. */
export const SPIN_TILT_MAX = 60;
/** 🧊 무대 높이만큼 끌면 몇 도 기우나 — 한 손가락 세로 끌기. */
export const SPIN_TILT_PER_HEIGHT = 120;
/** 🧊 두 손가락 확대의 아래·위 한계 — 너무 작으면 잃어버리고, 너무 크면 화면을 뚫는다. */
export const SPIN_ZOOM_MIN = 0.6;
export const SPIN_ZOOM_MAX = 2.5;
const DEFAULT_VIEW: SpinView = { tilt: 0, zoom: 1 };

/** 이 층의 3D 시점 — 없으면 정면·원래 크기. */
export function spinView(state: Pick<ArState, "view">, layerId: string): SpinView {
  return state.view?.[layerId] ?? DEFAULT_VIEW;
}

/**
 * 🧊 **이 층의 띠를 아직 안 받는가** — 3D 파일이 있고 아직 못 불러온 적이 없으면 띠를 **미뤄 둔다**(2026-09-14).
 *
 * 3D 가 서는 동안 띠는 안 보이는데도 세 화면이 미리 받고 있었다 — 수막새 띠 503KB, 교실 회선(1.5Mbps)에서 2.6초.
 * 숨어 있는 층도 투명하게 `<img>` 로 그려 두면 브라우저는 받는다 — 그리기도 미룬다.
 * 3D 를 못 불러오면(`modelFailed`) 그때 받아 띠로 돌아간다. 세 화면이 같은 규칙(previewParity).
 */
export function layerStripDeferred(
  models: Readonly<Record<string, string>> | undefined,
  layerId: string,
  modelFailed: boolean | undefined,
): boolean {
  return !!models?.[layerId] && !modelFailed;
}

/**
 * 🧊 **3D 무대 위 표시** — 지금 무엇을 보고 있나. 세 화면(책·카메라·관리자 미리보기)이 같은 말(previewParity).
 * 폰에서 「3D 가 안 나와요」를 가를 수 있게 한다.
 * 🔴 대신 그림(띠)이 없는데 「그림으로 보여 드려요」라고 하면 **거짓말**이다 — 3D 만 올린 장면(2026-09-14).
 */
/** 🧊 AR 보기 「④ 3D 모델」이 쓰는 층 id — 「3D 층 하나짜리 마법」의 그 층. */
export const AR_MODEL_LAYER = "model";

/**
 * 🧊 **AR 보기 「④ 3D 모델」 → 3D 층 하나짜리 마법** (2026-09-14 운영자 요청).
 *
 * 운영자가 마법 꾸미기(층·누르는 자리) 없이 `.glb` 하나만 올려도 카메라 위에 실물이 서고 손으로 돌려 보게 한다.
 * 새로 짜지 않고 **28쪽 수막새와 같은 모양**(새 화면·끌면 돌아가요·저절로 돌기)으로 만든다 — 그러면 무대(`mountModel`)·
 * 손짓(`spinDrag`·`spinTilt`·`spinPinch`)·표시(`modelModeLabel`)·출처(`fullCredits`)가 **운영에서 검증된 그대로** 따라온다.
 *
 * 🔴 고르는 순서: 이 장면에 마법이 있으면 **마법이 먼저**(`null`) — 운영자가 꾸민 것을 3D 한 장이 덮지 않는다.
 * 3D 주소가 없으면 `null`. 주소 거르기(불변식 7)는 부르는 쪽이 이미 한 것을 넘긴다.
 */
export function arModelMagic(
  hasMagic: boolean,
  modelUrl: string | null | undefined,
  credit?: string,
): { magic: ArInteract; models: Record<string, string> } | null {
  if (hasMagic || !modelUrl) return null;
  const c = credit?.trim();
  const magic = normalizeArInteract({
    layers: [
      { id: AR_MODEL_LAYER, label_ko: "실물 3D", startVisible: true, full: true, spin: true, spinAuto: true, ...(c ? { credit_ko: c } : {}) },
    ],
    hotspots: [],
  });
  return magic ? { magic, models: { [AR_MODEL_LAYER]: modelUrl } } : null;
}

export function modelModeLabel(modelOn: boolean, hasStrip: boolean): string {
  if (modelOn) return "🧊 실물 3D";
  return hasStrip ? "🖼️ 3D 를 못 불러와 그림으로 보여 드려요" : "⚠️ 3D 를 못 불러왔어요 — 잠시 뒤 다시 열어 주세요";
}

/**
 * 🧊 세로로 끈 만큼 기울인다(무대 높이 기준 비율, 아래로 끌면 양수 = 위에서 내려다본다).
 * 한계에 닿으면 거기서 멈춘다 — 돌아 넘어가지 않는다(한 바퀴 도는 것은 좌우 끌기뿐).
 */
export function spinTilt(state: ArState, layerId: string, dy: number): ArState {
  if (!Number.isFinite(dy) || dy === 0) return state;
  const cur = spinView(state, layerId);
  const tilt = Math.max(-SPIN_TILT_MAX, Math.min(SPIN_TILT_MAX, cur.tilt + dy * SPIN_TILT_PER_HEIGHT));
  if (tilt === cur.tilt) return state;
  return { ...state, view: { ...state.view, [layerId]: { ...cur, tilt } } };
}

/** 🧊 두 손가락 사이가 `factor` 배 벌어졌다 → 그만큼 키운다(한계 안). 1 이면 그대로. */
export function spinPinch(state: ArState, layerId: string, factor: number): ArState {
  if (!Number.isFinite(factor) || factor <= 0 || factor === 1) return state;
  const cur = spinView(state, layerId);
  const zoom = Math.max(SPIN_ZOOM_MIN, Math.min(SPIN_ZOOM_MAX, cur.zoom * factor));
  if (zoom === cur.zoom) return state;
  return { ...state, view: { ...state.view, [layerId]: { ...cur, zoom } } };
}

/**
 * 🔄 저절로 도는 띠의 **지금 각도** — 그림의 `transform: matrix(…, tx, …)` 에서 읽는다(`getComputedStyle`).
 * 띠는 `width: N×100%` 라 그림 폭이 자리 폭의 N배이고, 왼쪽으로 tx 만큼 밀린 것이 곧 지나간 장수다.
 * 못 읽으면(검사 환경·transform 없음) 0 = 정면.
 */
export function turnFromTransform(transform: string, layerWidthPx: number, frames: number): number {
  if (!(frames > 1) || !(layerWidthPx > 0)) return 0;
  const m = /matrix\(([^)]+)\)/.exec(transform);
  if (!m) return 0;
  const parts = m[1]!.split(",").map((s) => Number(s.trim()));
  const tx = parts[4];
  if (!Number.isFinite(tx)) return 0;
  const frame = Math.round(-tx! / layerWidthPx) % frames;
  return ((frame < 0 ? frame + frames : frame) % frames) / frames;
}

/** 🔄 지금 끌면 어느 층이 도나 — 새 화면에 보이는 `spin` 층 중 첫 것. 없으면 `null`(끌기는 아무 일도 안 한다). */
export function spinTarget(def: ArInteract, state: ArState): string | null {
  for (const l of fullLayersOn(def, state)) if (l.spin) return l.id;
  return null;
}

/** 돌린 각도(0~1) → 띠의 몇 번째 장인가. 장이 하나면 언제나 0. */
export function spinFrame(state: Pick<ArState, "spin">, layerId: string, frames: number): number {
  if (!(frames > 1)) return 0;
  const turn = state.spin?.[layerId] ?? 0;
  const k = Math.round(turn * frames) % frames;
  return k < 0 ? k + frames : k;
}

/**
 * 🏷 새 화면에 보이는 층들의 **출처 표기** — 화면 아래 한 줄. 같은 글은 한 번만.
 * 판정은 여기 한 곳: 어느 층이 보이는지는 `fullLayersOn` 과 같은 규칙(보이는 full 층).
 */
export function fullCredits(def: ArInteract, state: ArState): string[] {
  const out: string[] = [];
  for (const l of fullLayersOn(def, state)) {
    if (l.credit_ko && !out.includes(l.credit_ko)) out.push(l.credit_ko);
  }
  return out;
}

export function initialLayerState(def: ArInteract): ArState {
  const visible = new Set(def.layers.filter((l) => l.startVisible).map((l) => l.id));
  // 손잡이 양끝은 둘 다 살아 있어야 섞인다 — mix 로 투명도를 정한다
  if (def.slider) {
    visible.add(def.slider.a);
    visible.add(def.slider.b);
  }
  return { visible, mix: 0 };
}

/** 누른 뒤의 상태 — 원본을 바꾸지 않는다(미리보기가 되감기를 한다). */
export function applyAction(state: ArState, action: ArAction, hotspotId = ""): ArState {
  const visible = new Set(state.visible);
  const keep = state.plays ? { plays: state.plays } : {};
  if (action.kind === "show") {
    // 🔁 켜진 채로 또 켜면 「한 번 더」 — 누르기 **전에** 보이던 층만 센다(처음 켜는 건 화면이 알아서 붙인다)
    let plays = state.plays;
    for (const id of action.on) {
      if (state.visible.has(id)) plays = { ...plays, [id]: (plays?.[id] ?? 0) + 1 };
    }
    for (const id of action.off) visible.delete(id);
    for (const id of action.on) visible.add(id);
    const hint = action.guardian_ko ? { hint: { hotspot: hotspotId, guardian_ko: action.guardian_ko } } : {};
    return { visible, mix: state.mix, ...(plays ? { plays } : {}), ...hint };
  }
  return {
    visible,
    mix: state.mix,
    said: { hotspot: hotspotId, child_ko: action.child_ko, guardian_ko: action.guardian_ko, focus: action.focus },
    ...keep,
  };
}

/**
 * 🔁 **한 번만 도는 띠를 언제 새로 붙일까** — 세 화면이 `<img key>` 로 쓴다.
 * 꺼짐→켜짐일 때(보이기 전에 다 돌아 버리지 않게) + **켜진 채로 또 눌렀을 때**(`plays`).
 */
export function layerPlayKey(id: string, shown: number, state: Pick<ArState, "plays">): string {
  return `${id}:${shown > 0 ? "on" : "off"}:${state.plays?.[id] ?? 0}`;
}

/** 「돌아가기」 — 정의된 것이 있으면 그것, 없으면 처음 상태. */
export function goBack(def: ArInteract, state: ArState): ArState {
  return def.back ? applyAction({ ...state, said: undefined }, def.back) : initialLayerState(def);
}

/**
 * 🔴 **가장 가까운 자리** — 정확히 안 맞아도 `hitSlack` 안이면 눌린다.
 *
 * 한 손으로 폰을 들고 다른 손으로 누르면 그림이 흔들려서 **자리가 손가락 밑에서 움직인다.**
 * 0~7세면 더 그렇다. 붙은 층이 **지금 보일 때만** 후보다.
 * `x·y` 는 타깃 기준 0~1.
 */
export function hitHotspot(def: ArInteract, state: ArState, x: number, y: number): ArHotspot | null {
  let best: ArHotspot | null = null;
  let bestD = Infinity;
  for (const h of def.hotspots) {
    if (!state.visible.has(h.layer)) continue;
    const { x: rx, y: ry, w, h: rh } = h.rect;
    const dx = x < rx ? rx - x : x > rx + w ? x - (rx + w) : 0;
    const dy = y < ry ? ry - y : y > ry + rh ? y - (ry + rh) : 0;
    const d = Math.hypot(dx, dy);
    if (d <= AR_INTERACT_LIMITS.hitSlack && d < bestD) {
      best = h;
      bestD = d;
    }
  }
  return best;
}

/** 손잡이 값(0~1) → 두 층의 투명도. 서서히 섞이므로 번쩍임이 없다. */
export function sliderMix(def: ArInteract, mix: number): { a: number; b: number } | null {
  if (!def.slider) return null;
  const t = num(mix, 0, 0, 1);
  return { a: 1 - t, b: t };
}

/**
 * 🔴 **층 그림을 어디에 그리나** — 세 화면(책·카메라·관리자 미리보기)이 **이 하나**를 쓴다.
 *
 * 자리를 화면마다 다시 계산하면 「관리자에선 가운데인데 아이 화면은 구석」이 된다.
 * 자리가 없는 층은 쪽 전체 — 예전 「큰 판 한 장」 방식이 그대로 산다(하위호환).
 */
export function layerBox(l: ArLayerDef): { left: string; top: string; width: string; height: string } {
  const r = l.rect;
  if (!r) return { left: "0%", top: "0%", width: "100%", height: "100%" };
  return { left: `${r.x * 100}%`, top: `${r.y * 100}%`, width: `${r.w * 100}%`, height: `${r.h * 100}%` };
}

/**
 * 🖼️ 지금 **새 화면**인가 — 보이는 `full` 층들. 하나라도 있으면 새 화면 상태다.
 * 세 화면(책 뷰어·카메라 AR·관리자 미리보기)이 이걸로 「배경을 깔고, 이 층들만 그리고,
 * 이 층들에 붙은 자리만 누르게」 한다. 화면마다 따로 세면 갈린다.
 */
/**
 * 👆 **어디를 눌러야 하는지 알려 줄 자리들** — 지금 눌 수 있는 자리 그대로.
 *
 * ## 🔴 왜 필요한가
 * 누르는 자리는 **눈에 안 보이는 단추**다. 20쪽은 쪽 그림에 빛이 그려져 있어 짐작이 가지만,
 * 27쪽 물과 22쪽 보리는 아무 단서가 없다. 아이가 못 찾으면 **기능이 없는 것과 같다** —
 * 이 저장소가 「만들어 놓고 닿을 길이 없다」로 이미 세 번 겪은 모양이다.
 *
 * 그래서 잠깐 기다렸다가 그 자리에 **은은한 동그라미**를 띄운다. 판정은 여기 한 곳이고
 * (세 화면이 같은 자리에 띄운다), 그리는 일과 「언제 띄울까」는 화면이 한다.
 *
 * ## 🔴 한 번 누른 자리는 빼지 않는다
 * 뺐다가는 「아까 눌렀는데 또 뜬다」와 「눌렀더니 사라졌다」가 섞여 더 헷갈린다.
 * 화면이 **처음 한 번 누르면 그 쪽에서는 그만 띄우는** 쪽이 단순하고, 아이가 규칙을 안다.
 */
export function hintSpots(def: ArInteract, state: ArState): ArHotspot[] {
  const full = fullLayersOn(def, state);
  return def.hotspots.filter(
    (h) => state.visible.has(h.layer) && (full.length === 0 || full.some((l) => l.id === h.layer)),
  );
}

export function fullLayersOn(def: ArInteract, state: ArState): ArLayerDef[] {
  return def.layers.filter((l) => l.full && layerOpacity(def, state, l.id) > 0);
}

/**
 * 🔍 **새 화면에서 크게 볼 자리** — 위아래 바를 피한 가운데 상자.
 * 아래 끝 0.66 은 말풍선 위끝(0.688)보다 위다. 유물이 제 설명을 가리면 안 된다.
 */
export const MAGIC_ZOOM_BOX: ArRect = { x: 0.12, y: 0.16, w: 0.76, h: 0.5 };

/**
 * 🔍 **지금 크게 보고 있는 층** — 전시관에서 유물 하나를 눌렀을 때.
 *
 * ## 왜 필요한가
 * 유물을 눌러도 말풍선이 뜨고 **1.08배**가 전부였다. 박물관에 온 느낌을 내려면 「그 하나를
 * 꺼내 크게 보는」 단계가 있어야 한다. 새 개념을 만들지 않고 **이미 있는 `focus`** 를 쓴다 —
 * 운영자가 고칠 것도, 게시할 것도 없다.
 *
 * 🔴 **새 화면에서만.** 쪽 위에서는 층이 제자리에 있어야 한다(그림 위 그 자리가 뜻을 갖는다) —
 * 물속 얼굴을 가운데로 끌어내면 「물속」이 아니게 된다.
 */
export function zoomedLayer(def: ArInteract, state: ArState): string | null {
  const id = state.said?.focus;
  if (!id) return null;
  const on = fullLayersOn(def, state);
  return on.some((l) => l.id === id) ? id : null;
}

/**
 * 🔍 **말풍선을 닫는다** — 크게 본 유물도 제자리로.
 * 자리를 안 누른 곳을 눌렀을 때. 층은 그대로 두므로 「처음으로」와 다르다(전시관은 열린 채).
 */
export function dismissSaid(state: ArState): ArState {
  return state.said ? { visible: state.visible, mix: state.mix } : state;
}

/** 🔍 크게 볼 때 **뒤로 물러나는** 정도. 0 이면 사라져서 「어디로 갔지」가 되고, 1 이면 시선이 갈린다. */
export const MAGIC_ZOOM_DIM = 0.22;

/**
 * 👁 **지금 이 층을 화면에 얼마나 보여 줄까** — 세 화면이 부르는 **마지막** 판정.
 *
 * ## 🔴 왜 하나로 묶나 (2026-09-10)
 * 「보인다」에 겹치는 규칙이 셋이다: 켜짐/꺼짐(손잡이 포함) · 새 화면에선 쪽 위 층을 숨김 ·
 * 크게 볼 땐 뒤의 것이 물러남. 이걸 화면마다 이어 붙이고 있었더니 **세 벌이 됐고**,
 * 크게 보기를 넣자마자 「눌러 보는 검사」가 어긋남을 잡아냈다(검사는 shared 를 믿고 기대값을
 * 만든다). 겹치는 규칙은 **합친 자리**를 하나 만들어야 갈리지 않는다.
 */
export function layerShown(def: ArInteract, state: ArState, layerId: string): number {
  const l = def.layers.find((x) => x.id === layerId);
  if (!l) return 0;
  const base = layerOpacity(def, state, layerId);
  if (fullLayersOn(def, state).length > 0 && !l.full) return 0; // 새 화면 — 쪽 위 층은 숨는다
  const zoom = zoomedLayer(def, state);
  // 배경 층(자리 없음)은 안 물러난다 — 물러나면 「방」이 사라진다
  if (zoom && l.rect && zoom !== layerId) return base * MAGIC_ZOOM_DIM;
  return base;
}

/** 이 층이 지금 얼마나 보이나(0~1) — 손잡이 층은 섞인 값, 나머지는 켜짐/꺼짐. */
export function layerOpacity(def: ArInteract, state: ArState, layerId: string): number {
  const own = layerOpacityOwn(def, state, layerId);
  const me = def.layers.find((l) => l.id === layerId);
  if (!me?.follow) return own;
  // 🔗 따라가는 층 — 따르는 층이 보이는 만큼 × 제 스위치(한 단계뿐이라 재귀가 아니다)
  const t = layerOpacityOwn(def, state, me.follow);
  // 곡선: early 는 앞 절반에서 0→1, late 는 뒤 절반에서 0→1 — 손잡이 하나로 「창문 먼저, 탑은 나중에」
  const eased = me.followEase === "early" ? Math.min(1, t * 2) : me.followEase === "late" ? Math.max(0, t * 2 - 1) : t;
  return own * eased;
}
function layerOpacityOwn(def: ArInteract, state: ArState, layerId: string): number {
  if (def.slider?.a === layerId || def.slider?.b === layerId) {
    const m = sliderMix(def, state.mix)!;
    return def.slider.a === layerId ? m.a : m.b;
  }
  return state.visible.has(layerId) ? 1 : 0;
}
